import { isRESTAPIError } from '@gonfalon/errors/RESTAPIError';

import { MS_PER_DAY } from '../../constants';
import type { UsageSeriesList } from '../../types';
import { Granularity } from '../../types';

export const DAILY_WINDOW_LIMIT_MS = 62 * MS_PER_DAY;
const DEFAULT_RETRY_COUNT = 3;
const UNSUPPORTED_DESTINATION_GROUP_BY_MESSAGE = 'Invalid group by argument: destinationId';

export function isUnsupportedDestinationGroupBy(error: unknown) {
  return (
    isRESTAPIError(error) &&
    error.status === 400 &&
    error.code === 'invalid_request' &&
    error.message === UNSUPPORTED_DESTINATION_GROUP_BY_MESSAGE
  );
}

export function shouldRetryGroupedUsage(failureCount: number, error: unknown) {
  const isClientError = isRESTAPIError(error) && error.status >= 400 && error.status < 500;
  return !isClientError && failureCount < DEFAULT_RETRY_COUNT;
}

export function splitDailyRange(from: number, to: number, granularity: Granularity) {
  if (granularity !== Granularity.DAILY || to - from <= DAILY_WINDOW_LIMIT_MS) {
    return [{ from, to }];
  }
  const ranges = [];
  for (let start = from; start < to; start += DAILY_WINDOW_LIMIT_MS) {
    ranges.push({ from: start, to: Math.min(start + DAILY_WINDOW_LIMIT_MS, to) });
  }
  return ranges;
}

export function mergeUsageSeriesLists(lists: UsageSeriesList[]): UsageSeriesList {
  const offsets = lists.map((_, index) => lists.slice(0, index).reduce((sum, list) => sum + list.metadata.length, 0));
  return {
    ...lists[0],
    metadata: lists.flatMap((list) => list.metadata),
    series: lists.flatMap((list, index) =>
      list.series.map(({ time, ...values }) => ({
        time,
        ...Object.fromEntries(
          Object.entries(values).map(([key, value]) => [String(Number(key) + offsets[index]), value]),
        ),
      })),
    ),
  };
}
